import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { showToast } from '@/services/toast.service';
import TenantConfigService, { FormatLocale } from '@/services/tenantConfig.service';
import { useFeatureFlag, FeatureFlag } from '@/services/featureFlag.service';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import CustomDomainCard from './CustomDomainCard';
import OidcSsoCard from './OidcSsoCard';
import AllowedOriginsCard from './AllowedOriginsCard';

// Format locale presets
const FORMAT_LOCALE_OPTIONS: { label: string; value: FormatLocale }[] = [
  {
    label: 'US (1,234.56)',
    value: {
      decimal: '.',
      thousands: ',',
      grouping: [3],
      currency: ['$', ''],
    },
  },
  {
    label: 'Europe (1.234,56)',
    value: {
      decimal: ',',
      thousands: '.',
      grouping: [3],
      currency: ['', '€'],
    },
  },
];

export default function GeneralTab() {
  const queryClient = useQueryClient();
  const [tenantConfigId, setTenantConfigId] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [formatLocale, setFormatLocale] = useState<FormatLocale>({
    decimal: '.',
    thousands: ',',
    grouping: [3],
    currency: ['$', ''],
  });
  const [saving, setSaving] = useState(false);
  const showCustomDomain = useFeatureFlag(FeatureFlag.CUSTOM_DOMAIN);
  const { data: tenantConfig } = useTenantConfig();

  // Fetch tenant registration data
  const { data: registrationData, isLoading } = useQuery({
    queryKey: ['tenantRegistration'],
    queryFn: async () => {
      return await TenantConfigService.getInstance().getTenantRegistration();
    },
    staleTime: 5 * 60 * 1000,
  });

  // Initialize form with fetched data
  useEffect(() => {
    if (registrationData) {
      const { tenant_config, tenant_registration } = registrationData;
      setTenantConfigId(tenant_config.id);
      setTenantSlug(tenant_config.tenant_slug || '');
      setTenantName(tenant_registration?.name || tenant_config.tenant_name || '');

      if (tenant_config.format_locale) {
        setFormatLocale(tenant_config.format_locale);
      }
    }
  }, [registrationData]);

  const getFormatLocaleLabel = (locale: FormatLocale): string => {
    const match = FORMAT_LOCALE_OPTIONS.find(
      (opt) => JSON.stringify(opt.value) === JSON.stringify(locale)
    );
    return match?.label || 'Custom';
  };

  const saveSettings = useCallback(async () => {
    if (!tenantConfigId) return;

    setSaving(true);
    try {
      await TenantConfigService.getInstance().saveFormatLocale(
        tenantConfigId,
        formatLocale
      );
      showToast.success('Settings saved');
      queryClient.invalidateQueries({ queryKey: ['tenantRegistration'] });
      queryClient.invalidateQueries({ queryKey: ['tenantConfig'] });
    } catch (err: any) {
      showToast.error('Failed to save settings', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setSaving(false);
    }
  }, [tenantConfigId, formatLocale, queryClient]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      {/* Account Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
          <CardDescription>
            Basic information about your Pliable account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Account Name</Label>
            <Input value={tenantName} disabled className="bg-muted" />
          </div>

          <div className="space-y-2">
            <Label>Account ID</Label>
            <Input value={tenantSlug} disabled className="bg-muted font-mono" />
            <p className="text-sm text-muted-foreground">
              This identifier cannot be changed after creation
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Formatting Card */}
      <Card>
        <CardHeader>
          <CardTitle>Formatting</CardTitle>
          <CardDescription>
            Configure how numbers and currency values are displayed
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Number Format</Label>
            <Select
              value={getFormatLocaleLabel(formatLocale)}
              onValueChange={(label) => {
                const option = FORMAT_LOCALE_OPTIONS.find(
                  (opt) => opt.label === label
                );
                if (option) {
                  setFormatLocale(option.value);
                }
              }}
            >
              <SelectTrigger className="w-[250px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FORMAT_LOCALE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.label} value={opt.label}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Controls how numbers are formatted throughout the application
            </p>
          </div>

          <Button onClick={saveSettings} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Custom Domain Card */}
      {showCustomDomain && tenantConfigId && (
        <CustomDomainCard
          tenantConfigId={tenantConfigId}
          initialDomain={registrationData?.tenant_config.custom_domain || ''}
          initialCert={registrationData?.tenant_config.custom_domain_cert || null}
        />
      )}

      {/* OIDC SSO Card */}
      {tenantConfigId && (
        <OidcSsoCard
          tenantConfigId={tenantConfigId}
          customDomain={registrationData?.tenant_config.custom_domain || ''}
        />
      )}

      {/* Allowed Origins (iframe embed parents) */}
      {tenantConfigId && (
        <AllowedOriginsCard
          tenantConfigId={tenantConfigId}
          initialOrigins={registrationData?.tenant_registration?.embed_frame_ancestors || []}
        />
      )}
    </div>
  );
}
